/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { create } from 'zustand';

import { presetFor, type RolePreset } from '@/lib/roles';

/** Client-side "view as" role override for QA/support; UI-only (backend still authorizes on the real token), persisted to sessionStorage. */
interface RoleViewState {
  /** Effective role strings to gate on, or null to use the real token roles. */
  override: string[] | null;
  /** Id of the active persona preset, when the override came from one. */
  presetId: string | null;
  /** Apply a persona preset (or clear with null). */
  setPreset: (id: string | null) => void;
  /** Clear the override and fall back to the real token roles. */
  clear: () => void;
}

const STORAGE_KEY = 'edx-role-view';

interface Persisted {
  presetId: string | null;
}

function readStored(): Persisted {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { presetId: null };
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    return { presetId: typeof parsed.presetId === 'string' ? parsed.presetId : null };
  } catch {
    return { presetId: null };
  }
}

function persist(presetId: string | null): void {
  try {
    if (presetId) sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ presetId }));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
}

function overrideFor(preset: RolePreset | undefined): string[] | null {
  return preset ? [...preset.roles] : null;
}

const initial = readStored();
const initialPreset = presetFor(initial.presetId);

export const useRoleView = create<RoleViewState>((set) => ({
  override: overrideFor(initialPreset),
  presetId: initialPreset?.id ?? null,
  setPreset: (id) => {
    const preset = presetFor(id);
    const presetId = preset?.id ?? null;
    persist(presetId);
    set({ override: overrideFor(preset), presetId });
  },
  clear: () => {
    persist(null);
    set({ override: null, presetId: null });
  },
}));
